import type { CombinedState } from 'redux';
import { createSelector } from 'reselect';
import { DECODER_STATUS } from '@studio/constants/decoders';
import { ALL_VERSIONS } from '@studio/constants/monitors';
import { compareSemanticVersions } from '@studio/helpers/compareSemanticVersions';
import type { RootState } from '@studio/store/root-reducer';
import type { Monitors } from '@studio/types';
import { selectCustomerNameData } from '../../customer/selectors';
import type { MonitorState } from './types';

export const selectMonitorsState = (state: RootState): CombinedState<MonitorState> => state.admin.monitors;

const encoders = {
  errored: createSelector(selectMonitorsState, (state) => state.erroredEncoders.data),
  erroredCache: createSelector(selectMonitorsState, (state) => state.erroredEncoders.dataCache),
};

const decoders = {
  cache: createSelector(selectMonitorsState, (state) => state.decoders.dataCache),
  data: createSelector(selectMonitorsState, (state) => state.decoders.data),
  isFetching: createSelector(selectMonitorsState, (state) => state.decoders.isFetching),
  error: createSelector(selectMonitorsState, (state) => state.decoders.error),
  filter: createSelector(selectMonitorsState, (state) => state.decoders.filter),
  version: createSelector(selectMonitorsState, (state) => state.decoders.version),
  online: createSelector(
    selectMonitorsState,
    selectCustomerNameData,
    (state, customerNames): Monitors.Get.V2.Decoders[] => {
      return state.decoders.data.map((decoder) => {
        return {
          ...decoder,
          customerName: customerNames?.find((name: { name: string; uuid: string }) => decoder.fkCustomer === name.uuid)
            ?.name,
        };
      });
    }
  ),
};

const active = {
  decoders: createSelector(decoders.online, (state) => state.filter((e) => e.status === DECODER_STATUS.PLAYING)),
};

const filter = {
  decoders: {
    online: createSelector(
      decoders.online,
      decoders.filter,
      decoders.version,
      (currentDecoders, filter, playerVersion) =>
        currentDecoders
          .filter(
            (decoder) =>
              decoder.userName.toLowerCase().includes(filter.toLowerCase()) &&
              (decoder.playerVersion === playerVersion || playerVersion === ALL_VERSIONS)
          )
          .sort((a, b) => a.userName.localeCompare(b.userName))
    ),
    playing: createSelector(
      active.decoders,
      decoders.filter,
      decoders.version,
      (currentDecoders, filter, playerVersion) =>
        currentDecoders
          .filter(
            (decoder) =>
              decoder.userName.toLowerCase().includes(filter.toLowerCase()) &&
              (decoder.playerVersion === playerVersion || playerVersion === 'All Versions')
          )
          .sort((a, b) => a.userName.localeCompare(b.userName))
    ),
  },
};

const options = {
  decoders: {
    playing: createSelector(active.decoders, (state) =>
      [...new Set(state.map((e) => (e.playerVersion !== null ? e.playerVersion : '')))]
        .sort(compareSemanticVersions)
        .map((e) => {
          return { label: e, value: e };
        })
    ),
    online: createSelector(decoders.online, (state) =>
      [...new Set(state.map((e) => (e.playerVersion !== null ? e.playerVersion : '')))]
        .sort(compareSemanticVersions)
        .map((e) => {
          return { label: e, value: e };
        })
    ),
  },
  encoders: {
    errored: createSelector(encoders.errored, (state) =>
      [...new Set(state.map((e) => (e.version !== null ? e.version : '')))].sort(compareSemanticVersions).map((e) => {
        return { label: e, value: e };
      })
    ),
  },
};

export const select = {
  encoders,
  decoders,
  active,
  filter,
  options,
};
